/**
 * PetPolicy — Weidner Classic
 *
 * Tower 12 pet-policy page (https://www.tower12.com/pet-policy):
 *
 *   1. PETS intro — left: 10px rule + large gray "PETS" + blurb; right:
 *      four white gold-label stat cards (Pets Per Household, One Time Fee,
 *      Pet Deposit, Monthly Pet Rent).
 *   2. DOGS — left: eyebrow + gray "DOGS" + dog photo; right: two gold-
 *      label cards, two side-by-side breed lists, centered footnote.
 *   3. CATS — left: eyebrow + gray "CATS" + cat photo; right: two cards
 *      (No Restrictions / Pet Limit).
 *
 * Interior hero stays on HeroSolidBG in pages.json. Mustard labels
 * (#a37c20 = Tower 12 --color_1), gray display (#b6b6b6), and the 10px
 * left rule match LuxuryDefinition / Penthouse / FutureResidentsClub.
 *
 * Maintenance checklist when re-syncing with the rise parent:
 *   1. Diff this file against the rise PetPolicy in the sibling checkout.
 *   2. Do not reintroduce the rise restrictions callout or View Amenities CTA.
 *   3. Keep the three-block PETS / DOGS / CATS split (1/3 + 2/3).
 *   4. Keep gold-label white stat cards and the heading left-rule.
 *   5. Run `pnpm run lint:schema` and `pnpm run typecheck`.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  usePropertyField,
  useContentToken,
  useSlotGroup,
  useEditMode,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const MUSTARD = 'var(--vibe-color-secondary)';

const DEFAULT_DOG_IMAGE_URL =
  'https://irp.cdn-website.com/c035667c/dms3rep/multi/opt/dog-1920w.jpg';
const DEFAULT_CAT_IMAGE_URL =
  'https://irp.cdn-website.com/c035667c/dms3rep/multi/opt/cat-1920w.jpg';

const dogImageDef: SlotGroupDefinition = {
  label: 'Dogs Photo',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: DEFAULT_DOG_IMAGE_URL,
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'A puppy sitting on a wooden chair',
    },
  },
  generator: {
    prompt:
      'A photo of a dog that fits a luxury apartment pet-policy page — friendly, well-lit, not stock-clipart. Prefer a real community pet photo if one exists.',
    context:
      'Left column of the Dogs block on the Pet Policy page, under the DOGS heading. Different from the Cats photo below.',
  },
};

const catImageDef: SlotGroupDefinition = {
  label: 'Cats Photo',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: DEFAULT_CAT_IMAGE_URL,
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'A cat resting indoors',
    },
  },
  generator: {
    prompt:
      'A photo of a cat that fits a luxury apartment pet-policy page — friendly, well-lit, not stock-clipart. Prefer a real community pet photo if one exists.',
    context:
      'Left column of the Cats block on the Pet Policy page, under the CATS heading. Different from the Dogs photo above.',
  },
};

function hasValue(val: unknown): val is string {
  if (val === null || val === undefined) return false;
  const s = String(val).trim().toLowerCase();
  return (
    s !== '' &&
    s !== 'false' &&
    s !== 'null' &&
    s !== 'undefined' &&
    s !== '0' &&
    s !== 'none' &&
    s !== 'n/a' &&
    s !== 'na'
  );
}

function isExplicitlyFalsy(val: unknown): boolean {
  if (val === false) return true;
  if (val === null || val === undefined) return false;
  const s = String(val).trim().toLowerCase();
  return s === 'false' || s === 'no' || s === '0';
}

function formatCurrency(val: string): string {
  const num = parseFloat(val.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return val;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function PetPolicy() {
  const editMode = useEditMode();
  const allowsCats = usePropertyField('property_allows_cats');
  const allowsDogs = usePropertyField('property_allows_dogs');
  const propertyName = (usePropertyField('property_name') as string | null) || '';

  const dogImage = useSlotGroup('petpolicy.dogs_image', dogImageDef);
  const catImage = useSlotGroup('petpolicy.cats_image', catImageDef);

  const maxPetsValue = useContentToken('petpolicy.max_pets_value');
  const catsLimitValue = useContentToken('petpolicy.cats_limit_value');
  const maxWeightValue = useContentToken('petpolicy.max_weight_value');
  const petFeeValue = useContentToken('petpolicy.pet_fee_value');
  const petDepositValue = useContentToken('petpolicy.pet_deposit_value');
  const monthlyRentValue = useContentToken('petpolicy.monthly_rent_value');
  const dogsSpecificsValue = useContentToken('petpolicy.dogs_specifics_value');
  const catsSpecificsValue = useContentToken('petpolicy.cats_specifics_value');

  const dogsEyebrowRaw =
    useContentToken('petpolicy.dogs_eyebrow') || propertyName || 'Pet Policy';
  const catsEyebrowRaw =
    useContentToken('petpolicy.cats_eyebrow') || propertyName || 'Pet Policy';
  const dogsEyebrow = /pet policy/i.test(dogsEyebrowRaw)
    ? dogsEyebrowRaw
    : `${dogsEyebrowRaw} PET POLICY`;
  const catsEyebrow = /pet policy/i.test(catsEyebrowRaw)
    ? catsEyebrowRaw
    : `${catsEyebrowRaw} PET POLICY`;

  // Show species blocks unless the property explicitly disallows that pet.
  // Missing data → show (matches Tower 12, which always lists both).
  const showDogs = editMode || !isExplicitlyFalsy(allowsDogs);
  const showCats = editMode || !isExplicitlyFalsy(allowsCats);

  useRevealStyles();
  const introRef = useScrollReveal<HTMLElement>();
  const dogsRef = useScrollReveal<HTMLElement>();
  const catsRef = useScrollReveal<HTMLElement>();

  const maxPetsDisplay = hasValue(maxPetsValue) ? `${maxPetsValue} Allowed` : 'Allowed';
  const petFeeDisplay = hasValue(petFeeValue) ? formatCurrency(petFeeValue) : 'N/A';
  const petDepositDisplay = hasValue(petDepositValue) ? formatCurrency(petDepositValue) : 'N/A';
  const monthlyRentDisplay = hasValue(monthlyRentValue)
    ? formatCurrency(monthlyRentValue)
    : '$50';
  const maxWeightDisplay = hasValue(maxWeightValue)
    ? `${String(maxWeightValue).replace(/\s*lbs?\.?$/i, '')}LB Each`
    : '100LB Each';
  const catsLimitRaw = hasValue(catsLimitValue)
    ? catsLimitValue
    : hasValue(maxPetsValue)
      ? maxPetsValue
      : '2';
  const catsLimitDisplay = `${catsLimitRaw} Pets Allowed`;

  return (
    <>
      {/* ── PETS intro ─────────────────────────────────────────────── */}
      <section ref={introRef} className="pet-block pet-intro">
        <div className="pet-block__inner">
          <div className="pet-block__aside">
            <div className="pet-heading-block">
              <RichTextContent path="petpolicy.pets_heading" as="h2" className="pet-display" />
            </div>
            <RichTextContent
              path="petpolicy.description"
              label="Pet Policy Description"
              as="div"
              className="pet-intro__body"
            />
          </div>

          <div className="pet-block__main">
            <div className="pet-stats-row">
              <div className="pet-stat">
                <RichTextContent
                  path="petpolicy.max_pets_label"
                  as="h3"
                  className="pet-stat__label"
                />
                <EditableToken path="petpolicy.max_pets_value" as="p" className="pet-stat__value">
                  {maxPetsDisplay}
                </EditableToken>
              </div>
              <div className="pet-stat">
                <RichTextContent
                  path="petpolicy.pet_fee_label"
                  as="h3"
                  className="pet-stat__label"
                />
                <EditableToken
                  path="petpolicy.pet_fee_value"
                  as="p"
                  className="pet-stat__value"
                >
                  {petFeeDisplay}
                </EditableToken>
              </div>
              <div className="pet-stat">
                <RichTextContent
                  path="petpolicy.pet_deposit_label"
                  as="h3"
                  className="pet-stat__label"
                />
                <EditableToken
                  path="petpolicy.pet_deposit_value"
                  as="p"
                  className="pet-stat__value"
                >
                  {petDepositDisplay}
                </EditableToken>
              </div>
              <div className="pet-stat">
                <RichTextContent
                  path="petpolicy.monthly_rent_label"
                  as="h3"
                  className="pet-stat__label"
                />
                <EditableToken
                  path="petpolicy.monthly_rent_value"
                  as="p"
                  className="pet-stat__value"
                >
                  {monthlyRentDisplay}
                </EditableToken>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DOGS ───────────────────────────────────────────────────── */}
      {showDogs && (
        <section ref={dogsRef} className="pet-block pet-species">
          <div className="pet-block__inner">
            <div className="pet-block__aside">
              <div className="pet-heading-block">
                <EditableToken path="petpolicy.dogs_eyebrow" as="p" className="pet-eyebrow">
                  {dogsEyebrow}
                </EditableToken>
                <RichTextContent
                  path="petpolicy.dogs_heading"
                  as="h2"
                  className="pet-display pet-display--species"
                />
              </div>
              {(dogImage.image || editMode) && (
                <EditableSlotGroup groupPath="petpolicy.dogs_image" definition={dogImageDef}>
                  {dogImage.image ? (
                    <div className="pet-species__image">
                      <VibesitesImage
                        url={dogImage.image}
                        alt={dogImage.alt || ''}
                        className="pet-species__img"
                        objectFit="cover"
                        sizes="(max-width: 768px) 100vw, 33vw"
                      />
                    </div>
                  ) : (
                    <div className="pet-species__image pet-species__image--empty" />
                  )}
                </EditableSlotGroup>
              )}
            </div>

            <div className="pet-block__main">
              <div className="pet-stats-row">
                <div className="pet-stat">
                  <RichTextContent
                    path="petpolicy.dogs_specifics_label"
                    as="h3"
                    className="pet-stat__label"
                  />
                  <EditableToken
                    path="petpolicy.dogs_specifics_value"
                    as="p"
                    className="pet-stat__value"
                  >
                    {dogsSpecificsValue || '*Breed Restrictions Apply'}
                  </EditableToken>
                </div>
                <div className="pet-stat">
                  <RichTextContent
                    path="petpolicy.max_weight_label"
                    as="h3"
                    className="pet-stat__label"
                  />
                  <EditableToken
                    path="petpolicy.max_weight_value"
                    as="p"
                    className="pet-stat__value"
                  >
                    {maxWeightDisplay}
                  </EditableToken>
                </div>
              </div>

              <RichTextContent
                path="petpolicy.dogs_restrictions"
                label="Dog Breed Restrictions"
                as="div"
                className="pet-breeds"
              />

              <RichTextContent
                path="petpolicy.dogs_footnote"
                as="div"
                className="pet-footnote"
              />
            </div>
          </div>
        </section>
      )}

      {/* ── CATS ───────────────────────────────────────────────────── */}
      {showCats && (
        <section ref={catsRef} className="pet-block pet-species">
          <div className="pet-block__inner">
            <div className="pet-block__aside">
              <div className="pet-heading-block">
                <EditableToken path="petpolicy.cats_eyebrow" as="p" className="pet-eyebrow">
                  {catsEyebrow}
                </EditableToken>
                <RichTextContent
                  path="petpolicy.cats_heading"
                  as="h2"
                  className="pet-display pet-display--species"
                />
              </div>
              {(catImage.image || editMode) && (
                <EditableSlotGroup groupPath="petpolicy.cats_image" definition={catImageDef}>
                  {catImage.image ? (
                    <div className="pet-species__image">
                      <VibesitesImage
                        url={catImage.image}
                        alt={catImage.alt || ''}
                        className="pet-species__img"
                        objectFit="cover"
                        sizes="(max-width: 768px) 100vw, 33vw"
                      />
                    </div>
                  ) : (
                    <div className="pet-species__image pet-species__image--empty" />
                  )}
                </EditableSlotGroup>
              )}
            </div>

            <div className="pet-block__main">
              <div className="pet-stats-row">
                <div className="pet-stat">
                  <RichTextContent
                    path="petpolicy.cats_specifics_label"
                    as="h3"
                    className="pet-stat__label"
                  />
                  <EditableToken
                    path="petpolicy.cats_specifics_value"
                    as="p"
                    className="pet-stat__value"
                  >
                    {catsSpecificsValue || 'No Restrictions'}
                  </EditableToken>
                </div>
                <div className="pet-stat">
                  <RichTextContent
                    path="petpolicy.cats_limit_label"
                    as="h3"
                    className="pet-stat__label"
                  />
                  <EditableToken
                    path="petpolicy.cats_limit_value"
                    as="p"
                    className="pet-stat__value"
                  >
                    {catsLimitDisplay}
                  </EditableToken>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <style>{`
        .pet-block {
          padding: 3.75rem 0;
          background: #fff;
        }
        .pet-block.pet-species {
          background: #f7f7f7;
        }
        .pet-block.pet-species + .pet-block.pet-species {
          background: #fff;
        }
        .pet-block__inner {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1.25rem;
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: start;
        }
        @media (min-width: 768px) {
          .pet-block__inner {
            grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
            gap: 3rem;
            padding: 0 2rem;
            align-items: center;
          }
          .pet-species .pet-block__inner {
            align-items: start;
          }
        }
        @media (min-width: 1024px) {
          .pet-block__inner {
            padding: 0 3rem;
            gap: 4rem;
          }
        }

        /* 10px rule — same treatment as LuxuryDefinition / Penthouse */
        .pet-heading-block {
          border-left: 10px solid #54595f;
          padding: 0 0 0 1.25rem;
          margin-bottom: 1.25rem;
          text-align: left;
        }
        .pet-species .pet-heading-block {
          margin-bottom: 1.5rem;
        }

        .pet-display {
          font-family: var(--vibe-font-heading);
          font-size: clamp(3rem, 7vw, 5rem);
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0;
          text-transform: uppercase;
          color: #b6b6b6;
          margin: 0;
        }
        .pet-display--species {
          font-size: clamp(2.5rem, 5.5vw, 3.75rem);
          margin-top: 0.15rem;
        }
        .pet-display p {
          margin: 0;
        }

        .pet-eyebrow {
          font-family: var(--vibe-font-heading);
          font-size: clamp(0.95rem, 1.2vw, 1.15rem);
          font-weight: 400;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
          margin: 0;
          line-height: 1.3;
        }

        .pet-intro__body {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.75;
          color: #000;
          padding-left: calc(10px + 1.25rem);
        }
        .pet-intro__body p {
          margin: 0 0 0.75rem;
        }
        .pet-intro__body p:last-child {
          margin-bottom: 0;
        }

        .pet-stats-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }
        @media (min-width: 640px) {
          .pet-stats-row {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.25rem;
          }
        }

        .pet-stat {
          text-align: center;
          background: #fff;
          border-radius: 2px;
          padding: 1.75rem 1.25rem;
          box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
          min-height: 7.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .pet-species .pet-stat {
          /* Cards sit on the gray band — keep them white */
          background: #fff;
        }

        .pet-stat__label {
          font-family: var(--vibe-font-heading);
          font-size: clamp(0.9rem, 1.35vw, 1.25rem);
          font-weight: 500;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: ${MUSTARD};
          margin: 0 0 0.65rem;
          line-height: 1.25;
        }
        .pet-stat__label p {
          margin: 0;
        }
        .pet-stat__value {
          font-family: var(--vibe-font-body);
          font-size: clamp(0.9rem, 1.1vw, 1.05rem);
          font-weight: 500;
          color: #4b4d51;
          margin: 0;
          line-height: 1.4;
        }

        .pet-species__image {
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
        }
        .pet-species__image--empty {
          background: #e8e8e8;
          min-height: 12rem;
        }
        .pet-species__image .pet-species__img,
        .pet-species__image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .pet-breeds {
          margin-top: 2rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.65;
          color: #4b4d51;
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.25rem 2.5rem;
        }
        @media (min-width: 640px) {
          .pet-breeds {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        .pet-breeds ul {
          list-style: disc;
          margin: 0;
          padding-left: 1.25rem;
        }
        .pet-breeds li {
          margin-bottom: 0.4rem;
        }
        .pet-breeds p {
          margin: 0 0 0.75rem;
        }

        .pet-footnote {
          margin-top: 1.75rem;
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: clamp(0.85rem, 1vw, 1.05rem);
          font-weight: 500;
          line-height: 1.55;
          color: #4b4d51;
        }
        .pet-footnote p {
          margin: 0;
        }

        .pet-block {
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .pet-block.reveal--hidden {
          opacity: 0;
          transform: translateY(20px);
        }
        .pet-block.reveal--visible {
          opacity: 1;
          transform: translateY(0);
        }
        @media (prefers-reduced-motion: reduce) {
          .pet-block,
          .pet-block.reveal--hidden,
          .pet-block.reveal--visible {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.pets_heading': {
      label: 'Pets Heading',
      type: 'richtext',
      defaultValue: '<p>Pets</p>',
    },
    'petpolicy.description': {
      label: 'Pet Policy Description',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      suggestedFields: [
        'property_pet_policy_description',
        'property_petpolicy_info',
        'property_pets_blurb',
      ],
      defaultValue:
        "<p>We're not just pet-friendly, we LOVE pets and show it by providing a fenced-in pet exercise area, always having treats in the office and hosting frequent Yappy Hours…</p>",
    },
    'petpolicy.max_pets_label': {
      label: 'Pets Per Household Label',
      type: 'richtext',
      defaultValue: '<p>Pets Per Household</p>',
    },
    'petpolicy.max_pets_value': {
      label: 'Pets Per Household Value',
      type: 'text',
      defaultField: 'property_max_pets',
      defaultValue: '2',
      description:
        'Number of pets allowed per household. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_fee_label': {
      label: 'One Time Fee Label',
      type: 'richtext',
      defaultValue: '<p>One Time Fee</p>',
    },
    'petpolicy.pet_fee_value': {
      label: 'One Time Fee Value',
      type: 'text',
      defaultField: 'property_upfront_pet_fee',
      defaultValue: 'N/A',
      description:
        'One-time upfront pet fee. Pulled from the property automatically; override here to display a custom value. Shows "N/A" when there is no fee.',
    },
    'petpolicy.pet_deposit_label': {
      label: 'Pet Deposit Label',
      type: 'richtext',
      defaultValue: '<p>Pet Deposit</p>',
    },
    'petpolicy.pet_deposit_value': {
      label: 'Pet Deposit Value',
      type: 'text',
      defaultField: 'property_pet_deposit',
      defaultValue: 'N/A',
      description:
        'Pet deposit amount. Pulled from the property automatically; override here to display a custom value. Shows "N/A" when there is no deposit.',
    },
    'petpolicy.monthly_rent_label': {
      label: 'Monthly Pet Rent Label',
      type: 'richtext',
      defaultValue: '<p>Monthly Pet Rent</p>',
    },
    'petpolicy.monthly_rent_value': {
      label: 'Monthly Pet Rent Value',
      type: 'text',
      defaultField: 'property_monthly_pet_rent',
      defaultValue: '50',
      description:
        'Monthly pet rent amount. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.dogs_eyebrow': {
      label: 'Dogs Eyebrow',
      type: 'text',
      description:
        'Small uppercase line above the Dogs heading. Defaults to the property name; " PET POLICY" is appended when missing.',
      defaultField: 'property_name',
      defaultValue: '',
    },
    'petpolicy.dogs_heading': {
      label: 'Dogs Heading',
      type: 'richtext',
      defaultValue: '<p>Dogs</p>',
    },
    'petpolicy.dogs_specifics_label': {
      label: 'Dogs Policy Specifics Label',
      type: 'richtext',
      defaultValue: '<p>Pet Policy Specifics</p>',
    },
    'petpolicy.dogs_specifics_value': {
      label: 'Dogs Policy Specifics Value',
      type: 'text',
      defaultValue: '*Breed Restrictions Apply',
    },
    'petpolicy.max_weight_label': {
      label: 'Max Weight Label',
      type: 'richtext',
      defaultValue: '<p>Max Weight</p>',
    },
    'petpolicy.max_weight_value': {
      label: 'Max Weight Value',
      type: 'text',
      defaultField: 'property_max_pet_weight_limit',
      defaultValue: '100',
      description:
        'Maximum pet weight. Displayed as "{value}LB Each". Pulled from the property automatically.',
    },
    'petpolicy.dogs_restrictions': {
      label: 'Dog Breed Restrictions',
      type: 'richtext',
      defaultValue:
        '<ul><li>Pit Bull &amp; Pit Bull Mixes</li><li>American Pit bull Terrier</li><li>American Staffordshire Terrier</li><li>Wolf Hybrids</li><li>Doberman Pincher</li><li>Saint Bernard</li></ul><ul><li>American Bulldog</li><li>Staffordshire Bull Terrier</li><li>German Shepherd</li><li>Chow Chow</li><li>Great Dane</li><li>Akita</li></ul>',
      generator: {
        prompt:
          "Two HTML bullet lists of restricted dog breeds for this property's pet policy (six breeds each), matching the Tower 12 / Weidner restricted-breed layout. Use the property pet restrictions if available; otherwise the standard Weidner list.",
        context:
          'Two side-by-side disc lists under the Dogs stats on the Pet Policy page.',
        length: '12 short breed names across two <ul> lists',
      },
    },
    'petpolicy.dogs_footnote': {
      label: 'Dogs Restrictions Footnote',
      type: 'richtext',
      defaultValue:
        '<p>*Restricted Breed Dogs and mixes of these breeds are not permitted on any Weidner property. Additional breed or weight restrictions may apply.</p>',
    },
    'petpolicy.cats_eyebrow': {
      label: 'Cats Eyebrow',
      type: 'text',
      description:
        'Small uppercase line above the Cats heading. Defaults to the property name; " PET POLICY" is appended when missing.',
      defaultField: 'property_name',
      defaultValue: '',
    },
    'petpolicy.cats_heading': {
      label: 'Cats Heading',
      type: 'richtext',
      defaultValue: '<p>Cats</p>',
    },
    'petpolicy.cats_specifics_label': {
      label: 'Cats Policy Specifics Label',
      type: 'richtext',
      defaultValue: '<p>Pet Policy Specifics</p>',
    },
    'petpolicy.cats_specifics_value': {
      label: 'Cats Policy Specifics Value',
      type: 'text',
      defaultValue: 'No Restrictions',
    },
    'petpolicy.cats_limit_label': {
      label: 'Cats Pet Limit Label',
      type: 'richtext',
      defaultValue: '<p>Pet Limit</p>',
    },
    'petpolicy.cats_limit_value': {
      label: 'Cats Pet Limit Value',
      type: 'text',
      defaultField: 'property_max_pets',
      defaultValue: '2',
      description:
        'Pet limit shown in the Cats block (e.g. "2 Pets Allowed"). Defaults to the same max-pets property value.',
    },
  },
  slotGroups: {
    'petpolicy.dogs_image': {
      label: 'Dogs Photo',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultValue:
            'https://irp.cdn-website.com/c035667c/dms3rep/multi/opt/dog-1920w.jpg',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'A puppy sitting on a wooden chair',
        },
      },
      generator: {
        prompt:
          'A photo of a dog that fits a luxury apartment pet-policy page — friendly, well-lit, not stock-clipart. Prefer a real community pet photo if one exists.',
        context:
          'Left column of the Dogs block on the Pet Policy page, under the DOGS heading. Different from the Cats photo below.',
      },
    },
    'petpolicy.cats_image': {
      label: 'Cats Photo',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultValue:
            'https://irp.cdn-website.com/c035667c/dms3rep/multi/opt/cat-1920w.jpg',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'A cat resting indoors',
        },
      },
      generator: {
        prompt:
          'A photo of a cat that fits a luxury apartment pet-policy page — friendly, well-lit, not stock-clipart. Prefer a real community pet photo if one exists.',
        context:
          'Left column of the Cats block on the Pet Policy page, under the CATS heading. Different from the Dogs photo above.',
      },
    },
  },
};

(PetPolicy as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PetPolicy;
